import { useState, useEffect } from 'react';
import { Plus, Trash2, Eye, EyeOff, CheckCircle, XCircle } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import {
  getAllELDConnections,
  createELDConnection,
  updateELDConnection,
  deleteELDConnection,
  testProLogsConnectionById,
  type ELDConnection,
} from '../../services/eldConnectionsService';

export default function ELDConnectionsManager() {
  const [connections, setConnections] = useState<ELDConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  
  // Form state
  const [formData, setFormData] = useState<{
    name: string;
    provider: 'PRO_LOGS' | 'EVA_ELD' | 'SAMSARA';
    client_id: string;
    client_secret: string;
    enabled: boolean;
  }>({
    name: '',
    provider: 'PRO_LOGS',
    client_id: '',
    client_secret: '',
    enabled: true,
  });
  const [showSecret, setShowSecret] = useState(false);
  const [testingId, setTestingId] = useState<string | null>(null);

  const toast = useToast();

  useEffect(() => {
    loadConnections();
  }, []);

  const loadConnections = async () => {
    setLoading(true);
    try {
      const result = await getAllELDConnections();
      if (result.success && result.connections) {
        setConnections(result.connections);
      } else {
        toast.error(result.error || 'Failed to load connections');
      }
    } catch (error) {
      console.error('Error loading connections:', error);
      toast.error('Failed to load connections');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    // Trim credentials to avoid invalid_client errors from stray whitespace.
    // ProLogs secrets are long and easy to accidentally pad on paste.
    const cleanedFormData = {
      ...formData,
      client_id: formData.client_id.trim(),
      client_secret: formData.client_secret.trim(),
      name: formData.name.trim(),
    };

    try {
      if (editingId) {
        const result = await updateELDConnection(editingId, cleanedFormData);
        if (result.success) {
          toast.success('Connection updated successfully!');
          setEditingId(null);
        } else {
          toast.error(result.error || 'Failed to update connection');
        }
      } else {
        const result = await createELDConnection(cleanedFormData);
        if (result.success) {
          toast.success('Connection created successfully!');
          setShowAddForm(false);
        } else {
          toast.error(result.error || 'Failed to create connection');
        }
      }

      setFormData({
        name: '',
        provider: 'PRO_LOGS',
        client_id: '',
        client_secret: '',
        enabled: true,
      });
      
      await loadConnections();
    } catch (error) {
      console.error('Error saving connection:', error);
      toast.error('Failed to save connection');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (connection: ELDConnection) => {
    setFormData({
      name: connection.name,
      provider: connection.provider,
      client_id: connection.client_id || '',
      client_secret: connection.client_secret || '',
      enabled: connection.enabled,
    });
    setEditingId(connection.id);
    setShowAddForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this connection?')) {
      return;
    }

    setSaving(true);
    try {
      const result = await deleteELDConnection(id);
      if (result.success) {
        toast.success('Connection deleted successfully!');
        await loadConnections();
      } else {
        toast.error(result.error || 'Failed to delete connection');
      }
    } catch (error) {
      console.error('Error deleting connection:', error);
      toast.error('Failed to delete connection');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (id: string) => {
    setTestingId(id);
    try {
      const result = await testProLogsConnectionById(id);
      if (result.success) {
        toast.success(`Connection successful! Found ${result.data?.length || 0} trucks.`);
      } else {
        toast.error(`Connection failed: ${result.error}`);
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      toast.error('Failed to test connection');
    } finally {
      setTestingId(null);
    }
  };

  const handleToggleEnabled = async (connection: ELDConnection) => {
    setSaving(true);
    try {
      const result = await updateELDConnection(connection.id, {
        enabled: !connection.enabled,
      });
      if (result.success) {
        toast.success(`Connection ${!connection.enabled ? 'enabled' : 'disabled'}`);
        await loadConnections();
      } else {
        toast.error(result.error || 'Failed to update connection');
      }
    } catch (error) {
      console.error('Error toggling connection:', error);
      toast.error('Failed to update connection');
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    setShowAddForm(false);
    setEditingId(null);
    setFormData({
      name: '',
      provider: 'PRO_LOGS',
      client_id: '',
      client_secret: '',
      enabled: true,
    });
  };

  if (loading) {
    return <div className="text-center py-8">Loading connections...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-slate-900">ProLogs ELD Connections</h3>
        {!showAddForm && (
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Connection
          </button>
        )}
      </div>

      {/* Connection Form */}
      {showAddForm && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-6">
          <h4 className="text-md font-semibold text-slate-900 mb-4">
            {editingId ? 'Edit Connection' : 'Add New Connection'}
          </h4>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Connection Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Secondary ProLogs Account"
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Client ID
              </label>
              <input
                type="text"
                value={formData.client_id}
                onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                placeholder="2cd6ea7f-fd53-44ed-afdc-5513a7566f2d"
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Client Secret
              </label>
              <div className="relative">
                <input
                  type={showSecret ? 'text' : 'password'}
                  value={formData.client_secret}
                  onChange={(e) => setFormData({ ...formData, client_secret: e.target.value })}
                  placeholder="Enter Client Secret"
                  className="w-full px-4 py-2.5 pr-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowSecret(!showSecret)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showSecret ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="enabled"
                checked={formData.enabled}
                onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                className="w-4 h-4 text-green-600 border-slate-300 rounded focus:ring-green-500"
              />
              <label htmlFor="enabled" className="text-sm text-slate-700">
                Enable this connection
              </label>
            </div>

            <div className="flex gap-2">
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : editingId ? 'Update' : 'Add Connection'}
              </button>
              <button
                type="button"
                onClick={cancelEdit}
                className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Connections List */}
      <div className="space-y-3">
        {connections.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            No connections configured. Click "Add Connection" to get started.
          </div>
        ) : (
          connections.map((connection) => (
            <div
              key={connection.id}
              className="bg-white border border-slate-200 rounded-lg p-4 flex items-center justify-between"
            >
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <h4 className="font-semibold text-slate-900">{connection.name}</h4>
                  {connection.enabled ? (
                    <span className="flex items-center gap-1 text-sm text-green-600">
                      <CheckCircle className="w-4 h-4" />
                      Enabled
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-sm text-slate-500">
                      <XCircle className="w-4 h-4" />
                      Disabled
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-500 mt-1">
                  Client ID: {connection.client_id?.substring(0, 20)}...
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleTest(connection.id)}
                  disabled={testingId === connection.id || !connection.enabled}
                  className="px-3 py-1.5 text-sm bg-blue-50 text-blue-600 rounded hover:bg-blue-100 transition-colors disabled:opacity-50"
                >
                  {testingId === connection.id ? 'Testing...' : 'Test'}
                </button>
                <button
                  onClick={() => handleToggleEnabled(connection)}
                  className="px-3 py-1.5 text-sm bg-slate-100 text-slate-700 rounded hover:bg-slate-200 transition-colors"
                >
                  {connection.enabled ? 'Disable' : 'Enable'}
                </button>
                <button
                  onClick={() => handleEdit(connection)}
                  className="px-3 py-1.5 text-sm bg-slate-100 text-slate-700 rounded hover:bg-slate-200 transition-colors"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(connection.id)}
                  className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
